import { blogs_api_fetch_new_groups_url, blogs_api_groups_url } from 'mk/urls/functions';
import { forumCategoriesFetchTrigger } from 'mk2/apps/forum/containers/ForumCategories/ForumCategories.actions';
import { ForumCategoriesPageState } from 'mk2/apps/forum/containers/ForumCategories/ForumCategories.reducers';
import { fetchCategories } from 'mk2/apps/forum/containers/ForumCategories/ForumCategories.sagas';
import { getForumCategoriesPageState } from 'mk2/apps/forum/containers/ForumCategories/ForumCategories.selectors';
import { handleXHRGetErrorSaga, normalizeError, XHRAction, XHRGetError } from 'mk2/helpers/api';
import { getLogger } from 'mk2/logger';
import { LoadingState } from 'mk2/reducers/loadingState';
import { GroupEntity, GroupMemberEntity, GroupMemberSchema, GroupSchema } from 'mk2/schemas';
import { normalize } from 'normalizr';
import React from 'react';
import { all, call, fork, join, put, select, takeLatest } from 'redux-saga/effects';
import {
    listOfGroupsFetchApi,
    listOfGroupsFetchNewApi,
    FORUM_LIST_OF_GROUPS_FETCH_NEW,
    FORUM_LIST_OF_GROUPS_LOAD,
    ListOfGroupsFetchNewAction,
    ListOfGroupsLoadAction,
} from './ListOfGroups.actions';

const logger = getLogger('forum/ListOfGroups.sagas');

interface LoadApiResponse {
    body: {
        groups: GroupEntity[];
        newGroups: GroupEntity[];
        memberships: number[],
        askedToJoin: number[],
        favorites: number[],
        moderators: GroupMemberEntity[],
        hasNext: number;
        hasPrevious: number;
        count: number;
    };
}

function* load({ after, category, limitNew, query, xhr }: ListOfGroupsLoadAction & XHRAction) {
    yield put(listOfGroupsFetchApi.request(category, query, limitNew, after));

    // Check if categories are loaded
    const forumCategoriesState: ForumCategoriesPageState = yield select(getForumCategoriesPageState);
    let forumCategoriesTask;
    if (forumCategoriesState.loadingState === LoadingState.INIT) {
        // Trigger forum categories load
        forumCategoriesTask = yield fork(fetchCategories, {
            ...forumCategoriesFetchTrigger(),
            xhr,
        });
    }

    try {
        const response: LoadApiResponse = yield call(
            () => xhr.get(blogs_api_groups_url({
                type: 'all',
                category,
                query,
                limitNew,
                after,
            })),
        );

        const normalizedResponse = normalize({
            groups: response.body.groups,
            newGroups: response.body.newGroups,
            moderators: response.body.moderators,
        }, {
            groups: [GroupSchema],
            newGroups: [GroupSchema],
            moderators: [GroupMemberSchema],
        });

        if (forumCategoriesTask) {
            yield join(forumCategoriesTask);
        }

        yield put(listOfGroupsFetchApi.success(
            category,
            query,
            response.body.count,
            response.body.memberships,
            response.body.askedToJoin,
            response.body.favorites,
            limitNew,
            after,
            response.body.hasNext,
            response.body.hasPrevious,
            normalizedResponse,
        ));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(listOfGroupsFetchApi.failure(category, query, limitNew, after, normalizeError(error)));
    }
}

interface FetchNewApiResponse {
    body: {
        hasMore: boolean;
        newGroups: GroupEntity[];
        moderators: GroupMemberEntity[],
    };
}

function* fetchNew({ after, limit, xhr }: ListOfGroupsFetchNewAction & XHRAction) {
    yield put(listOfGroupsFetchNewApi.request(limit, after));

    try {
        const response: FetchNewApiResponse = yield call(
            () => xhr.get(blogs_api_fetch_new_groups_url({
                limit,
                after,
            })),
        );

        const normalizedResponse = normalize({
            moderators: response.body.moderators,
            newGroups: response.body.newGroups,
        }, {
            moderators: [GroupMemberSchema],
            newGroups: [GroupSchema],
        });

        yield put(listOfGroupsFetchNewApi.success(
            limit,
            after,
            response.body.hasMore,
            normalizedResponse,
        ));

    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(listOfGroupsFetchNewApi.failure(limit, after, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(FORUM_LIST_OF_GROUPS_LOAD, load),
        takeLatest(FORUM_LIST_OF_GROUPS_FETCH_NEW, fetchNew),
    ]);
}
